import React, { useEffect, useMemo, useState } from "react";
import { Grid, Layout, Menu, Tooltip } from "antd";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FileTextOutlined,
  IdcardOutlined,
  LineChartOutlined,
  LockOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MoonOutlined,
  ProfileOutlined,
  RobotOutlined,
  SunOutlined,
} from "@ant-design/icons";
import { Button } from "./Common";
import { logout, selectUser } from "../store/authSlice";
import { useTheme } from "../context/ThemeContext";
import useAccess from "../hooks/useAccess";

const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;

const PORTAL_GROUPS = [
  {
    key: "workspace",
    label: "Workspace",
    items: [
      {
        key: "/portal",
        icon: <RobotOutlined />,
        label: "My Agents",
      },
      {
        key: "/portal/usage",
        icon: <LineChartOutlined />,
        label: "Usage",
      },
    ],
  },
  {
    key: "billing",
    label: "Billing",
    items: [
      {
        key: "/portal/subscription",
        icon: <ProfileOutlined />,
        label: "Subscription",
      },
      {
        key: "/portal/invoices",
        icon: <FileTextOutlined />,
        label: "Invoices",
      },
    ],
  },
];

const ALL_PORTAL_ITEMS = PORTAL_GROUPS.flatMap(({ items }) => items);

const resolveSelectedKey = (pathname) => {
  const exact = ALL_PORTAL_ITEMS.find(({ key }) => key === pathname);
  if (exact) return exact.key;

  if (pathname.startsWith("/portal/agents/")) return "/portal";
  return pathname;
};

const focusMenuTrigger = () => {
  document.querySelector(".app-header__trigger")?.focus();
};

/**
 * Shell for client-portal users. A client never sees back-office navigation,
 * while routes and account behavior remain shared with the authenticated app.
 */
const PortalLayout = () => {
  const [collapsed, setCollapsed] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isBelowXl = !screens.xl;
  const { isDarkMode, toggleTheme } = useTheme();
  const { companyName } = useAccess();
  const user = useSelector(selectUser);

  useEffect(() => {
    setCollapsed(isBelowXl);
  }, [isBelowXl]);

  useEffect(() => {
    if (!isBelowXl || collapsed) return undefined;

    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      setCollapsed(true);
      focusMenuTrigger();
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [collapsed, isBelowXl]);

  const selectedKey = useMemo(
    () => resolveSelectedKey(location.pathname),
    [location.pathname]
  );

  const sectionLabel = useMemo(
    () =>
      ALL_PORTAL_ITEMS.find(({ key }) => key === selectedKey)?.label ?? "",
    [selectedKey]
  );

  const menuItems = useMemo(
    () =>
      PORTAL_GROUPS.map((group) => ({
        type: "group",
        key: "group-" + group.key,
        label: collapsed ? null : group.label,
        children: group.items.map(({ key, icon, label }) => ({
          key,
          icon,
          label: collapsed ? null : label,
          title: collapsed ? label : undefined,
          className: "app-menu__item",
        })),
      })),
    [collapsed]
  );

  const handleMenuClick = ({ key }) => {
    navigate(key || "/portal");
    if (isBelowXl) setCollapsed(true);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const openProfileWithKeyboard = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    navigate("/portal/profile");
  };

  const closeOverlay = () => {
    setCollapsed(true);
    focusMenuTrigger();
  };

  return (
    <Layout className="app-layout">
      {isBelowXl && !collapsed && (
        <button
          type="button"
          className="app-sider__backdrop"
          onClick={closeOverlay}
          aria-label="Close navigation"
        />
      )}

      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        theme={isDarkMode ? "dark" : "light"}
        width={isBelowXl ? 288 : 224}
        collapsedWidth={isBelowXl ? 0 : 64}
        breakpoint="xl"
        onBreakpoint={(broken) => setCollapsed(broken)}
        className={
          "app-sider" + (isBelowXl ? " app-sider--overlay" : "")
        }
        aria-label="Client portal sidebar"
      >
        <div
          className={
            "app-sider__logo" +
            (collapsed ? " app-sider__logo--collapsed" : "")
          }
        >
          <img
            src="/ERP.ico"
            className="app-sider__logo-img app-sider__logo-img--symbol"
            alt={collapsed ? "Kubit AI" : ""}
          />
          {!collapsed && (
            <div className="app-sider__brand-copy">
              <span className="app-sider__brand-title">Kubit AI</span>
              <span className="app-sider__brand-subtitle">Client Portal</span>
            </div>
          )}
        </div>

        <nav
          id="portal-navigation"
          className="app-sider__scroll"
          aria-label="Client portal navigation"
        >
          <Menu
            mode="inline"
            items={menuItems}
            selectedKeys={[selectedKey]}
            onClick={handleMenuClick}
            inlineIndent={12}
            className={
              "app-menu" + (collapsed ? " app-menu--collapsed" : "")
            }
          />
        </nav>

        <div className="app-sider__footer">
          <Button
            variant="danger"
            block
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            aria-label="Log out"
          >
            {!collapsed && "Logout"}
          </Button>
        </div>
      </Sider>

      <Layout className="app-layout__inner">
        <Header className="app-header">
          <Button
            variant="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed((current) => !current)}
            className="app-header__trigger"
            aria-label={collapsed ? "Open navigation" : "Close navigation"}
            aria-expanded={!collapsed}
            aria-controls="portal-navigation"
          />
          {sectionLabel && (
            <div className="app-header__title">
              <span className="app-header__title-text">{sectionLabel}</span>
              {!isMobile && companyName && (
                <span className="app-header__title-sub">{companyName}</span>
              )}
            </div>
          )}
          <div className="app-header__spacer" />
          <div className="app-header__right">
            <Tooltip title="My profile & security">
              <Button
                variant="text"
                icon={<IdcardOutlined />}
                onClick={() => navigate("/portal/profile")}
                aria-label="Open profile and security"
              />
            </Tooltip>
            <Tooltip title="Change password">
              <Button
                variant="text"
                icon={<LockOutlined />}
                onClick={() => navigate("/change-password")}
                aria-label="Change password"
              />
            </Tooltip>
            <Tooltip title={isDarkMode ? "Light mode" : "Dark mode"}>
              <Button
                variant="text"
                icon={isDarkMode ? <SunOutlined /> : <MoonOutlined />}
                onClick={toggleTheme}
                className="theme-toggle"
                aria-label={
                  isDarkMode ? "Switch to light mode" : "Switch to dark mode"
                }
              />
            </Tooltip>
            {!isMobile && user?.username && (
              <>
                <div className="app-header__divider" />
                <div
                  className="app-header__user"
                  title={user.username}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate("/portal/profile")}
                  onKeyDown={openProfileWithKeyboard}
                  aria-label={"Open profile for " + user.username}
                >
                  <div className="app-header__avatar">
                    {user.username.charAt(0)}
                  </div>
                  <div className="app-header__user-meta">
                    <span className="app-header__user-name">
                      {user.username}
                    </span>
                    <span className="app-header__user-role">Client portal</span>
                  </div>
                </div>
              </>
            )}
            {isMobile && (
              <Button
                variant="secondary"
                onClick={handleLogout}
                icon={<LogoutOutlined />}
                aria-label="Log out"
              >
                <span className="sr-only">Logout</span>
              </Button>
            )}
          </div>
        </Header>

        <Content className="app-content">
          <div className="app-content__inner">
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default PortalLayout;
